import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PumpProvider, usePump } from "@/context/PumpContext";
import Landing from "@/pages/Landing";
import Platform from "@/pages/Platform";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "@/components/ProtectedRoute";
import NotificationPoller from "@/components/NotificationPoller";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import StartShift from "@/pages/StartShift";
import RunningShift from "@/pages/RunningShift";
import EndShift from "@/pages/EndShift";
import ShiftSummary from "@/pages/ShiftSummary";
import PrintableReport from "@/pages/PrintableReport";
import MyShifts from "@/pages/MyShifts";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminShifts from "@/pages/admin/AdminShifts";
import AdminEmployees from "@/pages/admin/AdminEmployees";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminDailyReports from "@/pages/admin/AdminDailyReports";
import AdminDailyDetail from "@/pages/admin/AdminDailyDetail";
import AdminCash from "@/pages/admin/AdminCash";
import CreditReceipts from "@/pages/admin/CreditReceipts";
import CustomerMasterPage from "@/pages/admin/CustomerMasterPage";
import Expenses from "@/pages/admin/Expenses";
import CashBook from "@/pages/admin/CashBook";
import CreditInvoices from "@/pages/admin/CreditInvoices";
import Selfie from "@/pages/Selfie";
import Attendance from "@/pages/admin/Attendance";
import CreditMaster from "@/pages/admin/CreditMaster";
import CreditEntry from "@/pages/admin/CreditEntry";
import CreditCustomers from "@/pages/admin/CreditCustomers";

const MULTI_PUMP = process.env.REACT_APP_MULTI_PUMP === "true";

function RootRoute() {
  const { user, loading } = useAuth();
  const { pump, resolving } = usePump();
  if (loading || resolving) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;
  if (!user) return pump || !MULTI_PUMP ? <Navigate to="/login" replace /> : <Landing />;
  return <Navigate to={["admin", "manager"].includes(user.role) ? "/admin" : "/dashboard"} replace />;
}

function App() {
  return (
    <div className="App">
      <PumpProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<RootRoute />} />
            {MULTI_PUMP && <Route path="/select-pump" element={<Landing />} />}
            {MULTI_PUMP && <Route path="/platform" element={<Platform />} />}
            <Route path="/login" element={<Login />} />

            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/selfie" element={<ProtectedRoute><Selfie /></ProtectedRoute>} />
            <Route path="/admin/attendance" element={<ProtectedRoute admin><Attendance /></ProtectedRoute>} />
            <Route path="/my-shifts" element={<ProtectedRoute><MyShifts /></ProtectedRoute>} />
            <Route path="/shift/start" element={<ProtectedRoute><StartShift /></ProtectedRoute>} />
            <Route path="/shift/:id/running" element={<ProtectedRoute><RunningShift /></ProtectedRoute>} />
            <Route path="/shift/:id/end" element={<ProtectedRoute><EndShift /></ProtectedRoute>} />
            <Route path="/shift/:id/summary" element={<ProtectedRoute><ShiftSummary /></ProtectedRoute>} />
            <Route path="/shift/:id/print" element={<ProtectedRoute><PrintableReport /></ProtectedRoute>} />

            <Route path="/admin" element={<ProtectedRoute admin><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/shifts" element={<ProtectedRoute admin><AdminShifts /></ProtectedRoute>} />
            <Route path="/admin/daily" element={<ProtectedRoute admin><AdminDailyReports /></ProtectedRoute>} />
            <Route path="/admin/daily/:date" element={<ProtectedRoute admin><AdminDailyDetail /></ProtectedRoute>} />
            <Route path="/admin/cash" element={<ProtectedRoute admin><AdminCash /></ProtectedRoute>} />
            <Route path="/admin/credit/receipts" element={<ProtectedRoute admin><CreditReceipts /></ProtectedRoute>} />
            <Route path="/admin/customers" element={<ProtectedRoute admin><CustomerMasterPage /></ProtectedRoute>} />
            <Route path="/admin/expenses" element={<ProtectedRoute admin><Expenses /></ProtectedRoute>} />
            <Route path="/admin/cashbook" element={<ProtectedRoute admin><CashBook /></ProtectedRoute>} />
            <Route path="/admin/credit/invoices" element={<ProtectedRoute admin><CreditInvoices /></ProtectedRoute>} />
            <Route path="/admin/credit/master" element={<ProtectedRoute admin><CreditMaster /></ProtectedRoute>} />
            <Route path="/admin/credit/entry" element={<ProtectedRoute admin><CreditEntry /></ProtectedRoute>} />
            <Route path="/admin/credit/customers" element={<ProtectedRoute admin><CreditCustomers /></ProtectedRoute>} />
            <Route path="/admin/credit/customers/:id" element={<ProtectedRoute admin><CreditCustomers /></ProtectedRoute>} />
            <Route path="/admin/employees" element={<ProtectedRoute admin adminOnly><AdminEmployees /></ProtectedRoute>} />
            <Route path="/admin/settings" element={<ProtectedRoute admin adminOnly><AdminSettings /></ProtectedRoute>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <NotificationPoller />
        <Toaster position="top-center" richColors />
      </AuthProvider>
      </PumpProvider>
    </div>
  );
}

export default App;
